import React, { useMemo, useState } from 'react';
import { cn } from '../../utils/cn';
import { AFRICAN_COUNTRIES, PRIORITY_COUNTRIES_DRC } from '../../constants/countries';

/**
 * Numero de telephone en deux parties : indicatif pays (liste) et numero.
 *
 * La valeur reste UNE chaine « +243 999123456 », celle que le serveur stocke
 * et propage deja (telephoneMobile, Customer.phone). Une valeur existante sans
 * indicatif reconnu est conservee telle quelle dans le numero : aucun compte
 * existant ne voit son numero modifie a l'affichage.
 */

const DEFAULT_CODE = '+243';

// Indicatifs uniques, RDC et pays prioritaires d'abord, les plus longs testes
// en premier pour reconnaitre une valeur existante.
const OPTIONS = (() => {
  const prio = PRIORITY_COUNTRIES_DRC
    .map((code) => AFRICAN_COUNTRIES.find((c) => c.code === code))
    .filter(Boolean) as typeof AFRICAN_COUNTRIES;
  const rest = AFRICAN_COUNTRIES
    .filter((c) => !PRIORITY_COUNTRIES_DRC.includes(c.code))
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  return [...prio, ...rest].filter((c) => !!c.phoneCode);
})();
const CODES_BY_LENGTH = Array.from(new Set(OPTIONS.map((c) => c.phoneCode)))
  .sort((a, b) => b.length - a.length);

export function splitPhone(value?: string | null): { code: string; number: string } {
  const raw = (value || '').trim();
  if (!raw) return { code: DEFAULT_CODE, number: '' };
  const compact = raw.replace(/\s+/g, '');
  const code = CODES_BY_LENGTH.find((c) => compact.startsWith(c));
  if (!code) return { code: DEFAULT_CODE, number: raw };
  // Retire l'indicatif en gardant la mise en forme du reste du numero.
  let i = 0;
  let seen = 0;
  while (i < raw.length && seen < code.length) {
    if (!/\s/.test(raw[i])) seen += 1;
    i += 1;
  }
  return { code, number: raw.slice(i).trim() };
}

export function joinPhone(code: string, number: string): string {
  const n = number.trim();
  return n ? `${code} ${n}` : '';
}

interface PhoneFieldProps {
  label?: string;
  value?: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  hint?: React.ReactNode;
  className?: string;
}

export const PhoneField: React.FC<PhoneFieldProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  hint,
  className,
}) => {
  const parsed = useMemo(() => splitPhone(value), [value]);
  // Indicatif choisi avant de taper le numero : une valeur vide ne le porte
  // pas, on le garde donc localement.
  const [codeChoisi, setCodeChoisi] = useState(parsed.code);
  const number = parsed.number;
  const code = number ? parsed.code : codeChoisi;

  return (
    <div className={cn('space-y-1', className)}>
      {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}
      <div
        className={cn(
          'flex items-stretch min-h-[2.5rem] border border-gray-300 rounded-lg bg-white overflow-hidden',
          disabled ? 'bg-gray-50' : 'focus-within:ring-2 focus-within:ring-[#197ca8] focus-within:border-transparent',
        )}
      >
        <select
          aria-label="Indicatif pays"
          value={code}
          disabled={disabled}
          onChange={(e) => {
            setCodeChoisi(e.target.value);
            onChange(joinPhone(e.target.value, number));
          }}
          className={cn(
            'w-28 shrink-0 border-0 border-r border-gray-300 px-2 text-sm bg-transparent focus:outline-none',
            disabled ? 'text-gray-700' : 'text-gray-900 cursor-pointer',
          )}
        >
          {OPTIONS.map((c) => (
            <option key={c.code} value={c.phoneCode}>
              {c.flag} {c.phoneCode}
            </option>
          ))}
        </select>
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          value={number}
          disabled={disabled}
          placeholder={disabled ? 'Non renseigné' : '999 123 456'}
          onChange={(e) => onChange(joinPhone(code, e.target.value))}
          className="flex-1 min-w-0 border-0 px-3 py-2 text-sm text-gray-900 bg-transparent focus:outline-none"
        />
      </div>
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  );
};
